/**
 * Research Status admin page — live view of the company deep-dive
 * research queue at /admin/research-status.
 *
 * Shows the in-memory queue depth, worker liveness, DB counts by
 * research_status, a per-group pending breakdown, and recent
 * throughput. "Requeue pending now" re-runs the startup reconcile on
 * demand, so a stuck cohort can be kicked without a redeploy.
 * Auto-refreshes every 15s.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Gauge,
  Loader2,
  ListOrdered,
  RefreshCw,
  RotateCw,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

interface ResearchStatus {
  queue: {
    enabled: boolean;
    worker_alive: boolean;
    in_memory_depth: number;
    jobs_processed_since_restart: number;
    last_batch_started_at?: string | null;
    batch_size: number;
    batch_interval_seconds: number;
  };
  db: {
    by_status: Record<string, number>;
    pending_requested_total: number;
    pending_by_group: Array<{ group_name: string; count: number }>;
    researched_last_hour: number;
    researched_last_24h: number;
    last_researched_at?: string | null;
  };
  estimate: {
    batches_remaining: number;
    drain_eta_minutes: number;
  };
  generated_at: string;
}

const REFRESH_MS = 15_000;

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function fmtEta(minutes: number): string {
  if (!minutes) return '—';
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `~${h}h ${m}m` : `~${h}h`;
}

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: string;
}> = ({ icon, label, value, sub }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5">
    <div className="flex items-center gap-2 text-sm text-gray-500">
      {icon} {label}
    </div>
    <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
  </div>
);

const ResearchStatusPage: React.FC = () => {
  const [status, setStatus] = useState<ResearchStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [requeueing, setRequeueing] = useState(false);
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const resp = await api.get<ResearchStatus>('/admin/research-status');
      setStatus(resp.data);
    } catch (err: any) {
      if (!silent) {
        toast.error(err?.response?.data?.detail || 'Failed to load research status');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = window.setInterval(() => load(true), REFRESH_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [load]);

  const requeue = async () => {
    setRequeueing(true);
    try {
      const resp = await api.post('/admin/research-reconcile');
      toast.success(
        `Requeued ${resp.data?.requeued ?? 0} companies (queue depth ${resp.data?.queue_depth ?? '?'})`,
      );
      load(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Requeue failed');
    } finally {
      setRequeueing(false);
    }
  };

  const q = status?.queue;
  const db = status?.db;
  const healthy = !!(q?.enabled && q?.worker_alive);

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="h-6 w-6 text-blue-600" />
              Research Status
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Company deep-dive research queue — auto-refreshes every 15s.
              {status ? ` Updated ${fmtDate(status.generated_at)}.` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => load()}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={requeue}
              disabled={requeueing}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {requeueing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RotateCw className="h-4 w-4" />}
              Requeue pending now
            </button>
          </div>
        </div>

        {!status && loading ? (
          <div className="mt-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : status && q && db ? (
          <>
            {/* Health banner */}
            <div
              className={`mt-5 flex items-center gap-2 rounded-xl border p-4 text-sm ${
                healthy
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              {healthy ? (
                <>
                  <CheckCircle2 className="h-5 w-5" />
                  Worker running. {q.jobs_processed_since_restart} jobs processed
                  since last restart; batches of {q.batch_size} every{' '}
                  {Math.round(q.batch_interval_seconds / 60)} min.
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5" />
                  {!q.enabled
                    ? 'Research queue is DISABLED (check RESEARCH_QUEUE_ENABLED / COMPANY_RESEARCH_QUEUE_ENABLED).'
                    : 'Worker is not running — restart the service or hit "Requeue pending now" to kick it.'}
                </>
              )}
            </div>

            {/* Stat cards */}
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={<ListOrdered className="h-4 w-4" />}
                label="Queue depth"
                value={q.in_memory_depth}
                sub={`Pending in DB: ${db.pending_requested_total}`}
              />
              <StatCard
                icon={<Gauge className="h-4 w-4" />}
                label="Researched (last hour)"
                value={db.researched_last_hour}
                sub={`${db.researched_last_24h} in last 24h`}
              />
              <StatCard
                icon={<Clock className="h-4 w-4" />}
                label="Drain ETA"
                value={fmtEta(status.estimate.drain_eta_minutes)}
                sub={`${status.estimate.batches_remaining} batches remaining`}
              />
              <StatCard
                icon={<Activity className="h-4 w-4" />}
                label="Last researched"
                value={fmtDate(db.last_researched_at)}
                sub={`Last batch started: ${fmtDate(q.last_batch_started_at)}`}
              />
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {/* By status */}
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h2 className="text-sm font-semibold text-gray-900">
                  Companies by research status
                </h2>
                <table className="mt-3 w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {Object.entries(db.by_status).map(([s, count]) => (
                      <tr key={s}>
                        <td className="py-2 capitalize text-gray-700">
                          {s.replace('_', ' ')}
                        </td>
                        <td className="py-2 text-right font-medium text-gray-900">
                          {count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pending by group */}
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h2 className="text-sm font-semibold text-gray-900">
                  Pending backlog by group
                </h2>
                {db.pending_by_group.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">
                    No pending research-requested companies. 🎉
                  </p>
                ) : (
                  <table className="mt-3 w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {db.pending_by_group.map((g) => (
                        <tr key={g.group_name}>
                          <td className="py-2 text-gray-700">{g.group_name}</td>
                          <td className="py-2 text-right font-medium text-gray-900">
                            {g.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="mt-10 text-center text-sm text-gray-500">
            Could not load research status.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ResearchStatusPage;
